import type { FormView, Field } from '../../../ui/form/types';

const ROW_WIDTH = 12;

const separateFields = (fields: Field[]) => {
  const visibleFields = fields.filter(
    (field: Field) => !field.properties.hidden,
  );
  const hiddenFields = fields.filter((field: Field) => field.properties.hidden);

  return { visibleFields, hiddenFields };
};

const createRowMap = (
  visibleFields: Field[],
): { [rowIndex: number]: Field[] } => {
  const rowMap: { [rowIndex: number]: Field[] } = {};
  visibleFields.forEach((field: Field) => {
    const row = field.layout.row;
    if (!rowMap[row]) {
      rowMap[row] = [];
    }
    rowMap[row].push(field);
  });

  return rowMap;
};

const findRowsWithGaps = (rowMap: {
  [rowIndex: number]: Field[];
}): { [rowIndex: number]: Field[] } => {
  const rowsWithGaps: { [rowIndex: number]: Field[] } = {};
  Object.keys(rowMap).forEach((rowKey) => {
    const row = parseInt(rowKey, 10);
    const fieldsInRow = rowMap[row];
    const totalWidth = fieldsInRow.reduce(
      (sum, field) => sum + field.layout.width,
      0,
    );

    if (totalWidth !== ROW_WIDTH) {
      rowsWithGaps[row] = fieldsInRow;
    }
  });

  return rowsWithGaps;
};

const recalculateFieldLayouts = (
  fields: Field[],
  rowsWithGaps: { [rowIndex: number]: Field[] },
): Field[] => {
  return fields.map((field: Field) => {
    const row = field.layout.row;

    if (rowsWithGaps[row] && !field.properties.hidden) {
      const fieldsInRow = rowsWithGaps[row];
      const fieldCount = fieldsInRow.length;
      const newWidth = ROW_WIDTH / fieldCount;
      const fieldPosition = fieldsInRow.findIndex((f) => f.id === field.id);
      const newColumn = newWidth * fieldPosition;

      return {
        ...field,
        layout: {
          ...field.layout,
          width: newWidth,
          column: newColumn,
        },
      };
    }

    return field;
  });
};

const moveHiddenFieldsToBottom = (
  fields: Field[],
  visibleFields: Field[],
  hiddenFields: Field[],
): Field[] => {
  const maxVisibleRow = Math.max(
    ...visibleFields.map((field) => field.layout.row),
    -1,
  );

  return fields.map((field: Field) => {
    if (field.properties.hidden) {
      const hiddenFieldIndex = hiddenFields.findIndex((f) => f.id === field.id);
      return {
        ...field,
        layout: {
          ...field.layout,
          row: maxVisibleRow + 1 + hiddenFieldIndex,
        },
      };
    }

    return field;
  });
};

export const removeGaps = (baseForm: FormView): FormView => {
  const { visibleFields, hiddenFields } = separateFields(baseForm.fields);

  const rowMap = createRowMap(visibleFields);

  const rowsWithGaps = findRowsWithGaps(rowMap);

  const fieldsWithRecalculatedLayouts = recalculateFieldLayouts(
    baseForm.fields,
    rowsWithGaps,
  );

  const fieldsWithHiddenMoved = moveHiddenFieldsToBottom(
    fieldsWithRecalculatedLayouts,
    visibleFields,
    hiddenFields,
  );

  return {
    ...baseForm,
    fields: fieldsWithHiddenMoved,
  };
};
